/**
 * Shared helpers for role dashboards (Admin / Sales / Production).
 * Keep this file small — only truly cross-dashboard primitives.
 *
 * Port of atoms from /tmp/aura-design/extracted/aura-web-app/project/aura-shared.jsx,
 * trimmed to only what the 3 dashboards actually use.
 */

import { api } from '@/utils/api';

// ─── Formatters ───────────────────────────────────────────────────────────

/**
 * Compact INR formatter. ₹2.48 Cr / ₹92.4 L / ₹8,400.
 * Expects a raw rupee number.
 */
export function formatINRCompact(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '\u2014';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e7) return `${sign}\u20B9${(abs / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `${sign}\u20B9${(abs / 1e5).toFixed(2)} L`;
  if (abs >= 1e3) return `${sign}\u20B9${(abs / 1e3).toFixed(1)}K`;
  return `${sign}\u20B9${Math.round(abs).toLocaleString('en-IN')}`;
}

/**
 * Split a compact INR into numeral + unit for large-display rendering.
 * e.g. 24800000 → { num: '2.48', unit: 'Cr' }
 */
export function splitINRCompact(n: number | null | undefined): { num: string; unit: string } {
  if (n == null || !Number.isFinite(n)) return { num: '\u2014', unit: '' };
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e7) return { num: `${sign}\u20B9${(abs / 1e7).toFixed(2)}`, unit: 'Cr' };
  if (abs >= 1e5) return { num: `${sign}\u20B9${(abs / 1e5).toFixed(2)}`, unit: 'L' };
  if (abs >= 1e3) return { num: `${sign}\u20B9${(abs / 1e3).toFixed(1)}`, unit: 'K' };
  return { num: `${sign}\u20B9${Math.round(abs).toLocaleString('en-IN')}`, unit: '' };
}

export function formatNum(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '\u2014';
  return Math.round(n).toLocaleString('en-IN');
}

// ─── Generic fetch helper ────────────────────────────────────────────────

export interface FetchState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

/**
 * Wrap a promise to always return a uniform shape. Used inside widgets that
 * do their own fetching. On 404/5xx the widget falls through to the empty
 * state — never throws into the React tree.
 */
export async function safeGet<T = unknown>(path: string): Promise<{ data: T | null; error: string | null }> {
  try {
    const res = await api.get(path);
    // Most endpoints wrap payload in { success, data }. Unwrap when present.
    const raw = res?.data?.data ?? res?.data ?? null;
    return { data: raw as T, error: null };
  } catch (err: unknown) {
    const msg = (err as { response?: { status?: number }; message?: string })?.response?.status
      ? `HTTP ${(err as { response?: { status?: number } }).response?.status}`
      : 'network';
    return { data: null, error: msg };
  }
}

// ─── Common component-less atoms (return raw JSX via helpers) ────────────
// Intentionally NOT exported as components — the dashboards inline small
// visual helpers (Progress bar, Donut, Spark) directly so we can keep this
// file as plain TS without JSX.
